import {
  setBaseUrl,
  getAxios,
  Query as PublicQuery,
} from "@repo/rq-api-public";
import {
  isAnonymousSessionAtom,
  isAuthenticatedAtom,
  jotaiStore,
} from "../components/store";
import { ACCESS_TOKEN, ANONYMOUS_BOOKING_TOKEN } from "../utils/token";
import { exchangeBookingAnonymousToken } from "./anonymousBookingSession";

const baseUrl = import.meta.env.VITE_API_PROXY_HOST
  ? "/api"
  : import.meta.env.VITE_API_HOST;
setBaseUrl(baseUrl);

const axios = getAxios();

const PLATFORM_KEY = "platformKey";

function getApiKey(): string | undefined {
  const fromUrl = new URLSearchParams(window.location.search).get(PLATFORM_KEY);
  if (fromUrl) {
    localStorage.setItem(PLATFORM_KEY, fromUrl);
    return fromUrl;
  }

  return localStorage.getItem(PLATFORM_KEY) ?? import.meta.env.VITE_API_KEY;
}

axios.interceptors.request.use(
  (config) => {
    config.headers.ApiKey = getApiKey();

    const authToken = localStorage.getItem(ACCESS_TOKEN);
    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status !== 401 || !originalRequest) {
      return Promise.reject(error);
    }

    // Anonymous booking sessions use short-lived JWTs; re-exchange the stored anonymous token
    // and retry the request once before falling back to logout.
    // Skip re-exchange for token-exchange endpoints themselves to prevent recursive 401 handling.
    const isTokenExchangeUrl = originalRequest.url === PublicQuery.authenticationUserExchangeBookingAnonymousTokenPOSTUrl() || originalRequest.url === PublicQuery.authenticationUserExchangeMultibookingAnonymousTokenPOSTUrl();
    const anonymousToken = localStorage.getItem(ANONYMOUS_BOOKING_TOKEN);
    if (anonymousToken && !originalRequest._anonymousRetry && !isTokenExchangeUrl) {
      originalRequest._anonymousRetry = true;
      try {
        const accessToken = await exchangeBookingAnonymousToken(anonymousToken);
        if (!accessToken) throw new Error("Missing access token");
        localStorage.setItem(ACCESS_TOKEN, accessToken);
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return axios(originalRequest);
      } catch {
        // fall through to logout below
      }
    }

    localStorage.removeItem(ACCESS_TOKEN);
    localStorage.removeItem(ANONYMOUS_BOOKING_TOKEN);
    jotaiStore.set(isAuthenticatedAtom, false);
    jotaiStore.set(isAnonymousSessionAtom, false);
    return Promise.reject(error);
  }
);
